import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../stores/appStore';
import {
    getFullPlaylist,
    getTrackProgress,
    seekTo,
    togglePlayPause,
    skipToNextTrack,
    skipToPreviousTrack,
    playTrack,
} from '../services/audio';
import './MusicPlayerModal.css';

interface MusicPlayerModalProps {
    isOpen: boolean;
    onClose: () => void;
}

function formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatTrackName(path: string): string {
    if (!path) return '';
    return decodeURIComponent(path.split('/').pop()?.replace('.mp3', '') || '');
}

// Minimal SVG icons — no emoji
const IconPrev = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 6h2v12H6zm3.5 6 8.5 6V6z" />
    </svg>
);
const IconNext = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 18l8.5-6L6 6v12zm8.5-12v12H17V6h-2.5z" />
    </svg>
);
const IconPlay = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z" />
    </svg>
);
const IconPause = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
    </svg>
);
const IconShuffle = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M10.59 9.17 5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm.33 9.41-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z" />
    </svg>
);
const IconSequential = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6.99 11 3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z" />
    </svg>
);
const IconClose = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
);
const IconNote = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" opacity="0.7">
        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
    </svg>
);

export function MusicPlayerModal({ isOpen, onClose }: MusicPlayerModalProps) {
    // Subscribe directly to store — no polling, no manual listeners
    const nowPlayingTrack = useAppStore(s => s.nowPlayingTrack);
    const musicIsActive = useAppStore(s => s.musicIsActive);
    const musicIsPaused = useAppStore(s => s.musicIsPaused);
    const excludedTracks = useAppStore(s => s.excludedTracks);
    const setExcludedTracks = useAppStore(s => s.setExcludedTracks);
    const shuffleEnabled = useAppStore(s => s.shuffleEnabled);
    const toggleShuffle = useAppStore(s => s.toggleShuffle);

    const [progress, setProgress] = useState({ current: 0, duration: 0 });
    const [playlist, setPlaylist] = useState<string[]>([]);
    const trackListRef = useRef<HTMLDivElement>(null);

    // Progress bar polling (fine to keep local — changes every 250 ms)
    useEffect(() => {
        if (!isOpen) return;
        const tick = () => setProgress(getTrackProgress());
        tick();
        const id = setInterval(tick, 250);
        return () => clearInterval(id);
    }, [isOpen]);

    // Refresh playlist when modal opens
    useEffect(() => {
        if (isOpen) setPlaylist(getFullPlaylist());
    }, [isOpen]);

    // Scroll playing track into view when it changes
    useEffect(() => {
        if (!isOpen || !trackListRef.current) return;
        const playing = trackListRef.current.querySelector('.track-item.playing');
        playing?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [nowPlayingTrack, isOpen]);

    const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        seekTo(((e.clientX - rect.left) / rect.width) * progress.duration);
    }, [progress.duration]);

    const handleToggleExclude = useCallback((trackPath: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const isExcluded = excludedTracks.includes(trackPath);
        if (isExcluded) {
            setExcludedTracks(excludedTracks.filter(t => t !== trackPath));
        } else {
            if (playlist.length - excludedTracks.length <= 1) return;
            setExcludedTracks([...excludedTracks, trackPath]);
        }
    }, [excludedTracks, setExcludedTracks, playlist.length]);

    const currentTrackPath = useAppStore(s =>
        // derive path from nowPlayingTrack — compare display names
        playlist.find(p => formatTrackName(p) === s.nowPlayingTrack) ?? ''
    );

    const isPlaying = musicIsActive && !musicIsPaused;
    const activeTrackCount = playlist.length - excludedTracks.length;
    const progressPercent = progress.duration
        ? (progress.current / progress.duration) * 100
        : 0;

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                className="mpm-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <motion.div
                    className="mpm-sheet"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 40 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    onClick={e => e.stopPropagation()}
                >
                    {/* ── Header ── */}
                    <div className="mpm-header">
                        <button className="mpm-close" onClick={onClose} aria-label="Close">
                            <IconClose />
                        </button>
                        <span className="mpm-header-label">Now Playing</span>
                    </div>

                    {/* ── Art + track info ── */}
                    <div className="mpm-now-playing">
                        <div className={`mpm-art ${isPlaying ? 'spinning' : ''}`}>
                            <div className="mpm-art-inner">
                                <IconNote />
                            </div>
                        </div>

                        <div className="mpm-track-info">
                            <div className="mpm-track-name">
                                {nowPlayingTrack || 'No track playing'}
                            </div>
                            <div className="mpm-track-meta">
                                <span className={`mpm-status-dot ${isPlaying ? 'active' : ''}`} />
                                {isPlaying ? 'Playing' : musicIsActive ? 'Paused' : 'Stopped'}
                                &nbsp;·&nbsp;
                                {activeTrackCount} of {playlist.length} tracks
                            </div>
                        </div>
                    </div>

                    {/* ── Progress ── */}
                    <div className="mpm-progress-section">
                        <div className="mpm-progress-bar" onClick={handleProgressClick}>
                            <div className="mpm-progress-fill" style={{ width: `${progressPercent}%` }} />
                            <div className="mpm-progress-thumb" style={{ left: `${progressPercent}%` }} />
                        </div>
                        <div className="mpm-times">
                            <span>{formatTime(progress.current)}</span>
                            <span>{formatTime(progress.duration)}</span>
                        </div>
                    </div>

                    {/* ── Controls ── */}
                    <div className="mpm-controls">
                        <button className="mpm-btn" onClick={skipToPreviousTrack} aria-label="Previous">
                            <IconPrev />
                        </button>
                        <button className="mpm-btn mpm-btn-play" onClick={togglePlayPause} aria-label={isPlaying ? 'Pause' : 'Play'}>
                            {isPlaying ? <IconPause /> : <IconPlay />}
                        </button>
                        <button className="mpm-btn" onClick={skipToNextTrack} aria-label="Next">
                            <IconNext />
                        </button>
                    </div>

                    {/* ── Shuffle toggle ── */}
                    <button
                        className={`mpm-shuffle-btn ${shuffleEnabled ? 'active' : ''}`}
                        onClick={toggleShuffle}
                    >
                        {shuffleEnabled ? <IconShuffle /> : <IconSequential />}
                        <span>{shuffleEnabled ? 'Shuffle' : 'Sequential'}</span>
                    </button>

                    {/* ── Track list ── */}
                    <div className="mpm-track-list-header">
                        <span>Playlist</span>
                        <span>{activeTrackCount} active</span>
                    </div>

                    <div className="mpm-track-list" ref={trackListRef}>
                        {playlist.map((trackPath, index) => {
                            const name = formatTrackName(trackPath);
                            const isCurrent = trackPath === currentTrackPath;
                            const isExcluded = excludedTracks.includes(trackPath);
                            const isUnlocked = !trackPath.includes('/modern_pop/');

                            return (
                                <div
                                    key={trackPath}
                                    className={`track-item ${isCurrent ? 'playing' : ''} ${isExcluded ? 'excluded' : ''}`}
                                    onClick={() => playTrack(trackPath)}
                                >
                                    <div className="track-num">
                                        {isCurrent && isPlaying ? (
                                            <span className="track-bars">
                                                <span /><span /><span />
                                            </span>
                                        ) : (
                                            <span>{index + 1}</span>
                                        )}
                                    </div>

                                    <div className="track-text">
                                        <span className="track-name">{name}</span>
                                        {isUnlocked && <span className="track-unlocked-badge">Unlocked</span>}
                                    </div>

                                    <button
                                        className={`track-toggle ${!isExcluded ? 'on' : ''}`}
                                        onClick={e => handleToggleExclude(trackPath, e)}
                                        aria-label={isExcluded ? 'Include' : 'Exclude'}
                                        title={isExcluded ? 'Include in playlist' : 'Remove from playlist'}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

export default MusicPlayerModal;
